import { useMutation, MutationFunction, MutationOptions } from 'react-query';
import {
    createGnrRequest,
    updateGnrRequest,
    GnrRequest,
    GnrRequestResponse,
    UpdateGnrRequestBody,
    CreateGnrRequestBody,
    bulkUpdateGnrRequests,
    BulkUpdateGnrRequestBody,
    BulkUpdateGnrRequestQueries,
    archiveGnrRequest,
    ValidateDeliveryGnrRequestBody,
    ValidateDeliveryGnrRequestQuery,
    validateDeliveryGnrRequest,
} from '../../services/apiSLTP/sites/gnrRequests';

type UseUpdateGnrRequestMutationOptions = MutationOptions<GnrRequestResponse, Error, { gnrRequestId: string; data: UpdateGnrRequestBody }>;

type UseCreateGnrRequestMutationOptions = MutationOptions<GnrRequestResponse, Error, CreateGnrRequestBody>;

export const useUpdateGnrRequestMutation = (options?: UseUpdateGnrRequestMutationOptions) => {
    const gnrRequestMutation: MutationFunction<GnrRequestResponse, { gnrRequestId: string; data: UpdateGnrRequestBody }> = async ({
        gnrRequestId,
        data,
    }) => {
        try {
            const response: GnrRequestResponse = await updateGnrRequest(gnrRequestId, data);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during gnrRequest');
        }
    };

    return useMutation(gnrRequestMutation, options);
};

export const useCreateGnrRequestMutation = (options?: UseCreateGnrRequestMutationOptions) => {
    const gnrRequestMutation: MutationFunction<GnrRequestResponse, CreateGnrRequestBody> = async (data) => {
        try {
            const response: GnrRequestResponse = await createGnrRequest(data);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during gnrRequest');
        }
    };

    return useMutation(gnrRequestMutation, options);
};

type UseBulkUpdateGnrRequestsMutationOptions = MutationOptions<
    { message: string; modifiedCount: number },
    Error,
    { body: BulkUpdateGnrRequestBody; queries?: BulkUpdateGnrRequestQueries }
>;

export const useBulkUpdateGnrRequestsMutation = (options?: UseBulkUpdateGnrRequestsMutationOptions) => {
    const bulkUpdateGnrRequestsMutation: MutationFunction<
        { message: string; modifiedCount: number },
        { body: BulkUpdateGnrRequestBody; queries?: BulkUpdateGnrRequestQueries }
    > = async ({ body, queries }) => {
        try {
            const response = await bulkUpdateGnrRequests(body, queries);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during bulk update of gnrRequests');
        }
    };

    return useMutation(bulkUpdateGnrRequestsMutation, options);
};

type UseArchiveGnrRequestMutationOptions = MutationOptions<GnrRequestResponse, Error, { id: string }>;

export const useArchiveGnrRequestMutation = (options?: UseArchiveGnrRequestMutationOptions) => {
    const archiveGnrRequestMutation: MutationFunction<GnrRequestResponse, { id: string }> = async ({ id }) => {
        try {
            const response: GnrRequestResponse = await archiveGnrRequest(id);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during archive of gnrRequest');
        }
    };

    return useMutation(archiveGnrRequestMutation, options);
};

type UseValidateDeliveryGnrRequestMutationOptions = MutationOptions<
    GnrRequestResponse,
    Error,
    { gnrRequestId: string; body: ValidateDeliveryGnrRequestBody; queries?: ValidateDeliveryGnrRequestQuery; file?: File }
>;

export const useValidateDeliveryGnrRequestMutation = (options?: UseValidateDeliveryGnrRequestMutationOptions) => {
    const validateDeliveryGnrRequestMutation: MutationFunction<
        GnrRequestResponse,
        { gnrRequestId: string; body: ValidateDeliveryGnrRequestBody; queries?: ValidateDeliveryGnrRequestQuery; file?: File }
    > = async ({ gnrRequestId, body, queries, file }) => {
        try {
            return await validateDeliveryGnrRequest(gnrRequestId, body, queries, file);
        } catch (error) {
            throw new Error('An unexpected error occurred during delivery validation');
        }
    };

    return useMutation(validateDeliveryGnrRequestMutation, options);
};
